import { ChangeDetectionStrategy, Component, computed, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { NavigationEnd, Route, Router } from '@angular/router';
import { PlatformService } from 'src/app/services/platform.service';
import { UserService } from 'src/app/services/user.service';
import { role_home_path } from 'src/app/redirect-to-role-home';

@Component({
  selector: 'app-navigation-history',
  templateUrl: './navigation-history.component.html',
  styleUrls: ['./navigation-history.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconButton, MatIcon, MatTooltip, CdkConnectedOverlay],
})
export class NavigationHistoryComponent {
  private platform = inject(PlatformService);
  private router = inject(Router);
  private user_service = inject(UserService);
  private destroy_ref = inject(DestroyRef);

  protected can_go_back = signal(false);
  protected can_go_forward = signal(false);
  protected back_history_entries = signal<NavigationHistoryListItem[]>([]);
  protected forward_history_entries = signal<NavigationHistoryListItem[]>([]);
  protected history_menu_kind = signal<'back' | 'forward'>('back');
  protected history_menu_open = signal(false);
  protected menu_origin = signal<HTMLElement | null>(null);
  protected readonly menu_positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
  ];
  protected readonly history_menu_entries = computed(() => {
    if (this.history_menu_kind() === 'back') {
      return this.back_history_entries();
    }

    return this.forward_history_entries();
  });
  protected readonly history_menu_title = computed(() => {
    return this.history_menu_kind() === 'back' ? 'Historial hacia detrás' : 'Historial hacia delante';
  });
  protected readonly history_menu_icon = computed(() => {
    return this.history_menu_kind() === 'back' ? 'arrow_back' : 'arrow_forward';
  });

  private readonly on_popstate = (): void => {
    this.syncHistoryStateFromBrowser();
  };

  constructor() {
    if (this.platform.isElectronApp()) {
      const stop_listening = this.platform.onNavigationUpdate(() => {
        void this.refreshElectronHistory();
      });
      this.destroy_ref.onDestroy(stop_listening);
      void this.refreshElectronHistory();
      return;
    }

    this.syncHistoryStateFromBrowser();
    window.addEventListener('popstate', this.on_popstate);
    this.destroy_ref.onDestroy(() => window.removeEventListener('popstate', this.on_popstate));
    this.router.events.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.syncHistoryStateFromBrowser();
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.history_menu_open()) {
      this.closeHistoryMenu();
    }
  }

  goBack(): void {
    this.closeHistoryMenu();
    if (this.platform.isElectronApp()) {
      this.platform.goBack();
      return;
    }

    const navigation = getBrowserNavigation();
    if (navigation?.canGoBack) {
      navigation.back();
      return;
    }

    window.history.back();
  }

  goForward(): void {
    this.closeHistoryMenu();
    if (this.platform.isElectronApp()) {
      this.platform.goForward();
      return;
    }

    const navigation = getBrowserNavigation();
    if (navigation?.canGoForward) {
      navigation.forward();
      return;
    }

    window.history.forward();
  }

  openHistoryMenu(event: MouseEvent, kind: 'back' | 'forward'): void {
    event.preventDefault();
    event.stopPropagation();
    void this.openHistoryMenuAfterRefresh(event.currentTarget as HTMLElement, kind);
  }

  closeHistoryMenu(): void {
    this.history_menu_open.set(false);
  }

  goToHistoryIndex(index: number): void {
    this.closeHistoryMenu();

    if (this.platform.isElectronApp()) {
      this.platform.goToIndex(index);
      return;
    }

    const history_item = [...this.back_history_entries(), ...this.forward_history_entries()].find(entry => entry.index === index);
    const navigation = getBrowserNavigation();
    if (history_item?.key && navigation) {
      navigation.traverseTo(history_item.key);
    }
  }

  private async openHistoryMenuAfterRefresh(button: HTMLElement, kind: 'back' | 'forward'): Promise<void> {
    await this.refreshHistory();
    this.history_menu_kind.set(kind);
    if (this.history_menu_entries().length === 0) {
      this.closeHistoryMenu();
      return;
    }

    this.menu_origin.set(button);
    this.history_menu_open.set(true);
  }

  private async refreshHistory(): Promise<void> {
    if (this.platform.isElectronApp()) {
      await this.refreshElectronHistory();
      return;
    }

    this.syncHistoryStateFromBrowser();
  }

  private async refreshElectronHistory(): Promise<void> {
    this.can_go_back.set(await this.platform.canGoBack());
    this.can_go_forward.set(await this.platform.canGoForward());

    const history = await this.platform.getHistory();
    const items = history.entries.map((entry, index) => this.toHistoryListItem(entry, index));
    this.setHistoryLists(items, history.active_index);
  }

  private syncHistoryStateFromBrowser(): void {
    const navigation = getBrowserNavigation();
    if (!navigation) {
      this.can_go_back.set(window.history.length > 1);
      this.can_go_forward.set(false);
      this.back_history_entries.set([]);
      this.forward_history_entries.set([]);
      return;
    }

    this.can_go_back.set(navigation.canGoBack);
    this.can_go_forward.set(navigation.canGoForward);

    const entries = navigation.entries();
    const active_index = navigation.currentEntry?.index ?? entries.findIndex(entry => entry.key === navigation.currentEntry?.key);
    const items = entries.map((entry, fallback_index) =>
      this.toHistoryListItem(
        {
          url: entry.url,
          key: entry.key,
        },
        entry.index ?? fallback_index
      )
    );
    this.setHistoryLists(items, active_index);
  }

  private setHistoryLists(items: NavigationHistoryListItem[], active_index: number): void {
    this.back_history_entries.set(items.slice(0, Math.max(active_index, 0)).reverse().slice(0, MAX_HISTORY_MENU_ITEMS));
    this.forward_history_entries.set(items.slice(active_index + 1).slice(0, MAX_HISTORY_MENU_ITEMS));
  }

  private toHistoryListItem(entry: { url: string; key?: string | null }, index: number): NavigationHistoryListItem {
    const pathname = getAngularPathFromUrl(entry.url);
    const path_for_title = pathname === '/' ? role_home_path(this.user_service.getCurrentUser()?.role?.slug) : pathname;
    const route_title = titleForPath(this.router.config, path_for_title);
    const title = route_title ?? pathname;
    const subtitle = route_title && pathname !== '/' ? pathname : null;

    return {
      index,
      title,
      subtitle,
      url: entry.url,
      key: entry.key ?? null,
    };
  }
}

const MAX_HISTORY_MENU_ITEMS = 15;

interface NavigationHistoryListItem {
  index: number;
  title: string;
  subtitle: string | null;
  url: string;
  key: string | null;
}

interface BrowserNavigationEntry {
  url: string;
  key: string;
  index: number;
}

interface BrowserNavigation {
  readonly canGoBack: boolean;
  readonly canGoForward: boolean;
  readonly currentEntry: BrowserNavigationEntry | null;
  entries(): BrowserNavigationEntry[];
  back(): void;
  forward(): void;
  traverseTo(key: string): void;
}

function getBrowserNavigation(): BrowserNavigation | null {
  const navigation = (window as Window & { navigation?: BrowserNavigation }).navigation;
  return navigation ?? null;
}

function getAngularPathFromUrl(url: string): string {
  try {
    return normalizePath(new URL(url).pathname);
  } catch {
    return normalizePath(url);
  }
}

function normalizePath(path: string): string {
  const without_query = path.split('?')[0].split('#')[0];
  const with_slash = without_query.startsWith('/') ? without_query : `/${without_query}`;
  if (with_slash === '/') {
    return '/';
  }

  return with_slash.replace(/\/+$/, '');
}

function titleForPath(routes: Route[], pathname: string): string | null {
  const segments = pathname.split('/').filter(segment => segment.length > 0);
  return matchRouteTitle(routes, segments);
}

function matchRouteTitle(routes: Route[], segments: string[]): string | null {
  for (const route of routes) {
    if (route.redirectTo || route.path === '**') {
      continue;
    }

    const consumed = matchPath(route.path ?? '', segments);
    if (consumed === null) {
      continue;
    }

    const remaining = segments.slice(consumed);
    const child_title = route.children?.length ? matchRouteTitle(route.children, remaining) : null;
    if (child_title) {
      return child_title;
    }

    if (remaining.length === 0) {
      return typeof route.title === 'string' ? route.title : null;
    }
  }

  return null;
}

function matchPath(path: string, segments: string[]): number | null {
  if (path === '') {
    return 0;
  }

  const parts = path.split('/').filter(part => part.length > 0);
  if (parts.length > segments.length) {
    return null;
  }

  const matches = parts.every((part, index) => part.startsWith(':') || part === segments[index]);
  return matches ? parts.length : null;
}
